import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Calendar,
  CalendarCheck,
  Store,
  MessageCircle,
  MoreHorizontal,
  PlusCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';

// Quick access items for bottom nav
const quickNavAdmin = [
  { path: '/dashboard', label: 'Accueil', icon: LayoutDashboard },
  { path: '/calendar', label: 'Planning', icon: Calendar },
  { path: '/food-trucks', label: 'Adhérents', icon: Store },
  { path: '/messages', label: 'Messages', icon: MessageCircle },
];

const quickNavMember = [
  { path: '/dashboard', label: 'Accueil', icon: LayoutDashboard },
  { path: '/proposer-evenement', label: 'Proposer', icon: PlusCircle },
  { path: '/mes-services', label: 'Services', icon: CalendarCheck },
  { path: '/messages', label: 'Messages', icon: MessageCircle },
];

export default function MobileBottomNav() {
  const { user } = useAuth();
  const location = useLocation();
  const [showMore, setShowMore] = useState(false);

  // Don't show on login/register pages
  if (!user || location.pathname === '/login' || location.pathname === '/register') {
    return null;
  }

  // Un extra n'a que son espace : pas de barre de raccourcis
  if (user.role === 'staff_extra') return null;

  const quickNav = user?.role === 'membre' ? quickNavMember : quickNavAdmin;

  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E5E0DC] mobile-bottom-nav safe-area-bottom">
        <div className="flex items-center justify-around h-16 px-2">
          {quickNav.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
                           location.pathname.startsWith(item.path + '/');

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center flex-1 h-full transition-colors touch-active ${
                  isActive 
                    ? 'text-primary' 
                    : 'text-muted-foreground hover:text-secondary'
                }`}
                data-testid={`bottom-nav-${item.path.slice(1)}`}
              >
                <Icon 
                  className={`w-5 h-5 ${isActive ? 'stroke-[2.5]' : 'stroke-[1.5]'}`} 
                />
                <span className={`text-[10px] mt-1 ${isActive ? 'font-semibold' : 'font-medium'}`}>
                  {item.label}
                </span>
                {isActive && (
                  <div className="absolute bottom-0 w-10 h-1 bg-primary rounded-t-full" />
                )}
              </Link>
            );
          })}
          
          {/* More button */}
          <button
            onClick={() => setShowMore(true)}
            className="flex flex-col items-center justify-center flex-1 h-full text-muted-foreground hover:text-secondary transition-colors touch-active"
            data-testid="bottom-nav-more"
          >
            <MoreHorizontal className="w-5 h-5 stroke-[1.5]" />
            <span className="text-[10px] mt-1 font-medium">Plus</span>
          </button>
        </div>
      </nav>

      {/* More Sheet */}
      <Sheet open={showMore} onOpenChange={setShowMore}>
        <SheetContent side="bottom" className="h-auto max-h-[70vh] rounded-t-2xl">
          <SheetHeader>
            <SheetTitle>Menu</SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-4 gap-4 py-6">
            {user?.role === 'membre' ? (
              <>
                <QuickLink to="/mon-food-truck" icon={Store} label="Mon FT" onClick={() => setShowMore(false)} />
                <QuickLink to="/vivier" icon={Store} label="Extras" onClick={() => setShowMore(false)} />
                <QuickLink to="/mes-paiements" icon={Calendar} label="Paiements" onClick={() => setShowMore(false)} />
                <QuickLink to="/social-media" icon={MessageCircle} label="Social" onClick={() => setShowMore(false)} />
                <QuickLink to="/gallery" icon={Calendar} label="Galerie" onClick={() => setShowMore(false)} />
              </>
            ) : (
              <>
                <QuickLink to="/mon-food-truck" icon={Store} label="Mon FT" onClick={() => setShowMore(false)} />
                <QuickLink to="/vivier" icon={Store} label="Extras" onClick={() => setShowMore(false)} />
                <QuickLink to="/planning-global" icon={Calendar} label="Planning+" onClick={() => setShowMore(false)} />
                <QuickLink to="/hec-planning" icon={Calendar} label="HEC" onClick={() => setShowMore(false)} />
                <QuickLink to="/social-media" icon={MessageCircle} label="Social" onClick={() => setShowMore(false)} />
                <QuickLink to="/sondages" icon={Calendar} label="Sondages" onClick={() => setShowMore(false)} />
                <QuickLink to="/bilans" icon={Calendar} label="Bilans" onClick={() => setShowMore(false)} />
                {user?.role === 'admin' && <QuickLink to="/crm" icon={Calendar} label="CRM" onClick={() => setShowMore(false)} />}
                <QuickLink to="/payments" icon={Calendar} label="Paiements" onClick={() => setShowMore(false)} />
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Spacer for bottom nav */}
      <div className="lg:hidden h-16" />
    </>
  );
}

function QuickLink({ to, icon: Icon, label, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex flex-col items-center justify-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors touch-active"
    >
      <Icon className="w-6 h-6 text-primary mb-1" />
      <span className="text-xs font-medium text-secondary">{label}</span>
    </Link>
  );
}
